import LEAD_FIELDS from '@/Constants/LeadFormMeta'
import XReadMore from '@/Mantine/XReadMore'
import LeadFormDocumentsUploadDetail from '@/Pages/Form/Components/LeadFormDocumentsUploadDetail'
import LeadFormEditEducationDetail from '@/Pages/Form/Components/LeadFormEditEducationDetail'
import LeadFormLanguageAptitudeTestDetail from '@/Pages/Form/Components/LeadFormLanguageAptitudeTestDetail'
import LeadFormOthersDetail from '@/Pages/Form/Components/LeadFormOthersDetail'
import LeadFormPersonalDetail from '@/Pages/Form/Components/LeadFormPersonalDetail'
import LeadFormPreferenceDetail from '@/Pages/Form/Components/LeadFormPreferenceDetail'
import LeadFormVisaPassportDetail from '@/Pages/Form/Components/LeadFormVisaPassportDetail'
import LeadFormWorkExperienceDetail from '@/Pages/Form/Components/LeadFormWorkExperienceDetail'
import { RootState } from '@/Store'
import { updateCsrfToken, updateIsMobileView, updateTotalStep } from '@/Store/leadFormSlice'
import { Divider, HStack, Typography, VStack, XelaColor } from '@/XelaReact'
import { Paper, useMantineTheme } from '@mantine/core'
import { IconCircleCheck } from '@tabler/icons-react'
import { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import LeadForm = App.Models.LeadForm

const LeadFormEdit: FC<{ leadForm: LeadForm; csrfToken: string }> = ({ leadForm, csrfToken }) => {
  const theme = useMantineTheme()
  const dispatch = useDispatch()
  const leadFormStore = useSelector((state: RootState) => state.leadForm)
  const [isSubmit, setIsSubmit] = useState(false)

  const filteredGroups = Object.keys(leadForm.fields).filter(
    (group) => leadForm.fields[group as keyof typeof leadForm.fields].length > 0
  )

  useEffect(() => {
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      dispatch(updateIsMobileView(true))
    }
    dispatch(updateTotalStep(filteredGroups.length))
    dispatch(updateCsrfToken(csrfToken))
  }, [])

  useEffect(() => {
    if (leadFormStore.current_step === filteredGroups.length) {
      setIsSubmit(true)
    }
  }, [leadFormStore.current_step])

  return (
    <HStack
      className="lead-from-submit-container"
      style={{
        background: XelaColor.White,
      }}
    >
      <VStack
        spacing="8px"
        style={{
          padding: '20px 0',
          height: leadFormStore.is_mobile_view ? 'auto' : '100vh',
        }}
      >
        <HStack>
          <VStack spacing="2px">
            <Typography variant="headline">{leadForm.agency?.agency_name}</Typography>
            {leadForm.agency && leadForm.agency.agency_description && (
              <XReadMore content={leadForm.agency.agency_description} />
            )}
          </VStack>
          <HStack justifyContent="flex-end">
            <img
              src={leadForm.agency?.agency_logo}
              alt="logo"
              style={{
                height: leadForm.agency && leadForm.agency.agency_description ? '48px' : '32px',
              }}
            />
          </HStack>
        </HStack>
        <VStack spacing="24px" justifyContent={'center'} height="100%">
          <VStack height="100%" spacing="12px" justifyContent={'center'} alignItems={'center'}>
            {!isSubmit && (
              <VStack height="100%">
                {LEAD_FIELDS.map((leadFormMetaGroup) => {
                  if (
                    filteredGroups.includes(leadFormMetaGroup.group_name) &&
                    leadFormStore.current_step === filteredGroups.indexOf(leadFormMetaGroup.group_name)
                  ) {
                    return (
                      <VStack
                        height="100%"
                        key={leadFormMetaGroup.group_name}
                        style={{ padding: '25px', borderRadius: '30px' }}
                        bg={XelaColor.Gray12}
                        spacing="12px"
                      >
                        <HStack justifyContent="flex-start">
                          <HStack spacing="8px" justifyContent="space-between">
                            <Typography variant={'subheadline'}>{leadFormMetaGroup.group_label}</Typography>
                            <Typography variant={'subheadline'}>
                              Step {leadFormStore.current_step + 1}
                              <span style={{ color: XelaColor.Gray5 }}> of {filteredGroups.length}</span>
                            </Typography>
                          </HStack>
                        </HStack>
                        <HStack>
                          <Divider variant={'dotted'}></Divider>
                        </HStack>
                        <VStack spacing="12px" height="100%">
                          {leadFormMetaGroup.group_name === 'personal_details' && (
                            <LeadFormPersonalDetail
                              fields={leadForm.fields[leadFormMetaGroup.group_name]}
                              requirements={leadForm.requirements[leadFormMetaGroup.group_name]}
                              agencyId={leadForm.agency_id}
                              csrfToken={csrfToken}
                            />
                          )}
                          {leadFormMetaGroup.group_name === 'preference_details' && (
                            <LeadFormPreferenceDetail
                              agencyId={leadForm.agency_id}
                              requirements={leadForm.requirements[leadFormMetaGroup.group_name]}
                              fields={leadForm.fields[leadFormMetaGroup.group_name]}
                            />
                          )}
                          {leadFormMetaGroup.group_name === 'education_details' && (
                            <LeadFormEditEducationDetail
                              agencyId={leadForm.agency_id}
                              requirements={leadForm.requirements[leadFormMetaGroup.group_name]}
                              fields={leadForm.fields[leadFormMetaGroup.group_name]}
                            />
                          )}
                          {leadFormMetaGroup.group_name === 'language_aptitude_tests' && (
                            <LeadFormLanguageAptitudeTestDetail
                              fields={leadForm.fields[leadFormMetaGroup.group_name]}
                              requirements={leadForm.requirements[leadFormMetaGroup.group_name]}
                            />
                          )}
                          {leadFormMetaGroup.group_name === 'visa_passport_details' && (
                            <LeadFormVisaPassportDetail
                              fields={leadForm.fields[leadFormMetaGroup.group_name]}
                              requirements={leadForm.requirements[leadFormMetaGroup.group_name]}
                            />
                          )}
                          {leadFormMetaGroup.group_name === 'work_experiences' && (
                            <LeadFormWorkExperienceDetail
                              fields={leadForm.fields[leadFormMetaGroup.group_name]}
                              requirements={leadForm.requirements[leadFormMetaGroup.group_name]}
                            />
                          )}
                          {leadFormMetaGroup.group_name === 'others' && (
                            <LeadFormOthersDetail
                              fields={leadForm.fields[leadFormMetaGroup.group_name]}
                              requirements={leadForm.requirements[leadFormMetaGroup.group_name]}
                            />
                          )}
                          {leadFormMetaGroup.group_name === 'documents_upload' && (
                            <LeadFormDocumentsUploadDetail
                              agencyId={leadForm.agency_id}
                              fields={leadForm.fields[leadFormMetaGroup.group_name]}
                            />
                          )}
                        </VStack>
                      </VStack>
                    )
                  }
                  return null
                })}
              </VStack>
            )}
            {isSubmit && (
              <VStack height="75vh" justifyContent="center" alignItems="center">
                <Paper
                  p="xl"
                  radius="lg"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing.md,
                  }}
                  shadow="xl"
                >
                  <IconCircleCheck color={theme.colors.green[6]} size={24} />
                  <Typography variant="body">Your form has been successfully updated.</Typography>
                </Paper>
              </VStack>
            )}
          </VStack>
        </VStack>
        {!leadFormStore.is_mobile_view && (
          <VStack spacing="12px" alignItems={'flex-end'} justifyContent={'flex-end'} height="40px">
            <Typography variant="subheadline" style={{ color: XelaColor.Gray9 }}>
              Powered by SwiftAMS
            </Typography>
          </VStack>
        )}
      </VStack>
    </HStack>
  )
}

export default LeadFormEdit
